import { CloseOutlined, MessageOutlined } from '@ant-design/icons'
import { inject, observer } from 'mobx-react'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  IframeContainer,
  LauncherButton,
  LoadingOverlay,
  PanelHeader,
  PanelWrapper,
  ResizeHandle,
  TabList,
} from './ChatbotWidgetStyled'
import config from '../../config'
const { chatbotUrl } = config

const CHATBOT_BASE_URL = chatbotUrl
const PANEL_ID = 'chatbot-widget-panel'
const INITIAL_LOADING_DURATION = 10_000
const MIN_PANEL_WIDTH = 380
const MIN_PANEL_HEIGHT = 560
const PANEL_HORIZONTAL_GUTTER = 32
const PANEL_VERTICAL_GUTTER = 160
const PANEL_RESIZE_STEP = 40
const USER_NAME_HARDCODED = ''

const CHATBOT_TABS = [
  {
    id: 'internal-info',
    title: 'Thông tin nội bộ',
    iframeTitle: 'Tra cứu thông tin nội bộ',
    chatbotId: '4376fe1d-acba-4ba1-b7aa-b256b968e6cc',
  },
  {
    id: 'task-management',
    title: 'Công việc/nhiệm vụ',
    iframeTitle: 'Quản lý công việc/nhiệm vụ',
    chatbotId: 'f67c5577-51a9-4ed9-9bea-929e0460675f',
  },
  {
    id: 'document-summary',
    title: 'Tổng hợp văn bản',
    iframeTitle: 'Tra cứu, tổng hợp thông tin văn bản',
    chatbotId: '00539185-9798-4107-b5fb-0b328c524a02',
  },
]

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const getPanelMaxSize = () => {
  if (typeof window === 'undefined') {
    return { maxWidth: MIN_PANEL_WIDTH, maxHeight: MIN_PANEL_HEIGHT }
  }

  return {
    maxWidth: Math.max(
      MIN_PANEL_WIDTH,
      window.innerWidth - PANEL_HORIZONTAL_GUTTER
    ),
    maxHeight: Math.max(
      MIN_PANEL_HEIGHT,
      window.innerHeight - PANEL_VERTICAL_GUTTER
    ),
  }
}

const clampPanelSize = size => {
  const { maxWidth, maxHeight } = getPanelMaxSize()
  return {
    width: clamp(size.width, MIN_PANEL_WIDTH, maxWidth),
    height: clamp(size.height, MIN_PANEL_HEIGHT, maxHeight),
  }
}

const encodeUsername = username => {
  if (!username || typeof window === 'undefined' || !window.btoa) return ''

  const normalizedUsername = String(username)

  try {
    return window.btoa(unescape(encodeURIComponent(normalizedUsername)))
  } catch (e) {
    return window.btoa(normalizedUsername)
  }
}

const ChatbotWidget = props => {
  const { authenticationStore, commonStore } = props
  const theme = commonStore?.appTheme
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)
  const [isInitialDelayDone, setIsInitialDelayDone] = useState(false)
  const [loadedTabs, setLoadedTabs] = useState({})
  const [activeTab, setActiveTab] = useState(CHATBOT_TABS[0].id)
  const [isResizing, setIsResizing] = useState(false)
  const [panelSize, setPanelSize] = useState({
    width: MIN_PANEL_WIDTH,
    height: MIN_PANEL_HEIGHT,
  })
  const panelSizeRef = useRef(panelSize)
  const resizeStateRef = useRef(null)

  useEffect(() => {
    panelSizeRef.current = panelSize
  }, [panelSize])

  useEffect(() => {
    const handleWindowResize = () => {
      setPanelSize(prev => clampPanelSize(prev))
    }

    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    const handleKeyDown = event => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    if (!hasOpened || isInitialDelayDone) return undefined
    const timer = setTimeout(
      () => setIsInitialDelayDone(true),
      INITIAL_LOADING_DURATION
    )
    return () => clearTimeout(timer)
  }, [hasOpened, isInitialDelayDone])

  const handleResizeMove = useCallback(event => {
    const resizeState = resizeStateRef.current
    if (!resizeState) return

    event.preventDefault()
    setPanelSize(
      clampPanelSize({
        width: resizeState.width + resizeState.startX - event.clientX,
        height: resizeState.height + resizeState.startY - event.clientY,
      })
    )
  }, [])

  const stopResize = useCallback(() => {
    resizeStateRef.current = null
    setIsResizing(false)
    window.removeEventListener('pointermove', handleResizeMove)
    window.removeEventListener('pointerup', stopResize)
    window.removeEventListener('pointercancel', stopResize)
  }, [handleResizeMove])

  const startResize = useCallback(
    event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return

      event.preventDefault()
      // eslint-disable-next-line no-unused-expressions
      event.currentTarget.setPointerCapture?.(event.pointerId)
      resizeStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        ...panelSizeRef.current,
      }
      setIsResizing(true)
      window.addEventListener('pointermove', handleResizeMove)
      window.addEventListener('pointerup', stopResize)
      window.addEventListener('pointercancel', stopResize)
    },
    [handleResizeMove, stopResize]
  )

  useEffect(() => {
    return () => {
      resizeStateRef.current = null
      window.removeEventListener('pointermove', handleResizeMove)
      window.removeEventListener('pointerup', stopResize)
      window.removeEventListener('pointercancel', stopResize)
    }
  }, [handleResizeMove, stopResize])

  const handleResizeKeyDown = event => {
    const updates = {
      ArrowLeft: { width: PANEL_RESIZE_STEP },
      ArrowRight: { width: -PANEL_RESIZE_STEP },
      ArrowUp: { height: PANEL_RESIZE_STEP },
      ArrowDown: { height: -PANEL_RESIZE_STEP },
    }
    const update = updates[event.key]

    if (!update) return

    event.preventDefault()
    setPanelSize(prev =>
      clampPanelSize({
        width: prev.width + (update.width || 0),
        height: prev.height + (update.height || 0),
      })
    )
  }

  const username =
    authenticationStore?.currentUser?.username ||
    authenticationStore?.currentUser?.user_name
  const encodedUsername = useMemo(
    () => encodeUsername(USER_NAME_HARDCODED || username),
    [username]
  )
  const activeChatbot = CHATBOT_TABS.find(tab => tab.id === activeTab)
  const showInitialLoading =
    hasOpened && (!isInitialDelayDone || !loadedTabs[activeTab])

  const iframeSrcById = useMemo(() => {
    return CHATBOT_TABS.reduce((result, tab) => {
      result[tab.id] = `${CHATBOT_BASE_URL}/${
        tab.chatbotId
      }?hh_username=${encodeURIComponent(encodedUsername)}`
      return result
    }, {})
  }, [encodedUsername])

  const toggle = () => {
    setIsOpen(prev => {
      const next = !prev
      if (next) setHasOpened(true)
      return next
    })
  }

  const close = () => setIsOpen(false)

  if (!username) return null

  return (
    <>
      <PanelWrapper
        id={PANEL_ID}
        role="dialog"
        aria-modal="false"
        aria-label="Trợ lý ảo"
        tabIndex={-1}
        theme={theme}
        className={isResizing ? 'chatbot-widget-resizing' : undefined}
        style={{
          display: isOpen ? 'flex' : 'none',
          width: panelSize.width,
          height: panelSize.height,
        }}>
        <ResizeHandle
          type="button"
          aria-label="Thay đổi kích thước khung chat"
          title="Kéo để thay đổi kích thước"
          onPointerDown={startResize}
          onKeyDown={handleResizeKeyDown}
        />
        <PanelHeader theme={theme}>
          <span>Trợ lý ảo</span>
          <button
            type="button"
            className="chatbot-close"
            onClick={close}
            aria-label="Đóng">
            <CloseOutlined />
          </button>
        </PanelHeader>
        <TabList role="tablist" aria-label="Danh sách chatbot" theme={theme}>
          {CHATBOT_TABS.map(tab => {
            const isActive = tab.id === activeTab
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`${PANEL_ID}-${tab.id}-tab`}
                aria-selected={isActive}
                aria-controls={`${PANEL_ID}-${tab.id}-panel`}
                className={`chatbot-tab${
                  isActive ? ' chatbot-tab-active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.title}
              </button>
            )
          })}
        </TabList>
        <IframeContainer>
          {hasOpened &&
            CHATBOT_TABS.map(tab => {
              const isActive = tab.id === activeTab
              return (
                <iframe
                  key={tab.id}
                  id={`${PANEL_ID}-${tab.id}-panel`}
                  role="tabpanel"
                  aria-labelledby={`${PANEL_ID}-${tab.id}-tab`}
                  className={
                    showInitialLoading && isActive
                      ? 'chatbot-iframe-loading'
                      : ''
                  }
                  src={iframeSrcById[tab.id]}
                  title={tab.iframeTitle}
                  allow="microphone; clipboard-write"
                  aria-hidden={!isActive || showInitialLoading}
                  tabIndex={!isActive || showInitialLoading ? -1 : undefined}
                  style={{ display: isActive ? 'block' : 'none' }}
                  onLoad={() =>
                    setLoadedTabs(prev => ({ ...prev, [tab.id]: true }))
                  }
                />
              )
            })}
          {showInitialLoading && (
            <LoadingOverlay role="status" aria-live="polite" theme={theme}>
              <span className="chatbot-loading-spinner" aria-hidden="true" />
              <span>
                Đang tải {activeChatbot?.iframeTitle || 'Trợ lý ảo'}...
              </span>
            </LoadingOverlay>
          )}
        </IframeContainer>
      </PanelWrapper>
      <LauncherButton
        type="button"
        onClick={toggle}
        aria-label={isOpen ? 'Đóng chat' : 'Mở chat'}
        aria-expanded={isOpen}
        aria-controls={PANEL_ID}
        theme={theme}>
        {isOpen ? <CloseOutlined /> : <MessageOutlined />}
      </LauncherButton>
    </>
  )
}

ChatbotWidget.propTypes = {}

export default memo(
  inject('authenticationStore', 'commonStore')(observer(ChatbotWidget))
)
